import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function HowItWorksPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"client" | "pro">("client");

  const clientSteps = [
    {
      num: "01",
      icon: "🔍",
      color: "from-blue-500 to-blue-600",
      title: "Tell us what you need",
      description: "Describe your job — add photos, set your timing, and share your location. The more detail, the better the matches.",
      details: ["Free to post", "Takes 2 minutes", "Add up to 10 photos"]
    },
    {
      num: "02",
      icon: "⚡",
      color: "from-yellow-500 to-orange-500",
      title: "Get matched with local pros",
      description: "Your request goes out to Houston pros in your category. Interested pros respond with quotes you can compare.",
      details: ["Local Houston pros", "Compare quotes side-by-side", "Read client reviews"]
    },
    {
      num: "03",
      icon: "💬",
      color: "from-purple-500 to-pink-500",
      title: "Chat & choose",
      description: "Message pros directly. Ask questions, request photos of past work, and pick the one that feels right.",
      details: ["In-app messaging", "View portfolios", "Free quotes"]
    },
    {
      num: "04",
      icon: "✅",
      color: "from-emerald-500 to-teal-600",
      title: "Job done, review shared",
      description: "Your pro completes the work and you pay them directly. Then rate your experience to help other homeowners.",
      details: ["Pay your pro directly", "Rate & review", "Message history saved"]
    },
  ];

  const proSteps = [
    {
      num: "01",
      icon: "📝",
      color: "from-blue-500 to-blue-600",
      title: "Create your profile",
      description: "Sign up free, add your services, photos, and pricing. Get verified to stand out.",
      details: ["Free to join", "Takes 5 minutes", "Verification fast-track"]
    },
    {
      num: "02",
      icon: "📬",
      color: "from-yellow-500 to-orange-500",
      title: "Receive matched leads",
      description: "Get notifications when clients in your service area request your skills. Choose the jobs you want.",
      details: ["Pay only for leads you accept", "No monthly fees", "Real customers, ready to hire"]
    },
    {
      num: "03",
      icon: "💼",
      color: "from-purple-500 to-pink-500",
      title: "Send quotes & messages",
      description: "Use templates to respond fast. Build relationships with clients through in-app chat.",
      details: ["Saved quote templates", "Auto-fill client details", "Track response rate"]
    },
    {
      num: "04",
      icon: "💰",
      color: "from-emerald-500 to-teal-600",
      title: "Get paid & grow",
      description: "Complete jobs, earn 5-star reviews, and watch your business grow. Top pros earn $5K+ monthly.",
      details: ["Direct deposit", "Build reputation", "Priority placement"]
    },
  ];

  const steps = activeTab === "client" ? clientSteps : proSteps;

  return (
    <div className="min-h-screen pb-12">
      {/* Hero */}
      <div className="bg-gradient-to-br from-mouna-blue via-blue-600 to-navy text-white">
        <nav className="px-4 py-4 flex items-center justify-between max-w-6xl mx-auto">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-white">
            <svg width="32" height="32" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="hiwLg1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#4C6EF5"/><stop offset="100%" stopColor="#1A3CE8"/></linearGradient>
                <linearGradient id="hiwLg2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#20C997"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
                <linearGradient id="hiwBg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#3B5BDB"/><stop offset="100%" stopColor="#0E8C6A"/></linearGradient>
              </defs>
              <rect width="52" height="52" rx="14" fill="url(#hiwBg)"/>
              <g transform="translate(4,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.95"/><circle cx="11" cy="10" r="5" fill="url(#hiwLg1)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
              <g transform="translate(25,5)"><path d="M11,0 C4.9,0 0,4.9 0,11 C0,17.1 11,28 11,28 C11,28 22,17.1 22,11 C22,4.9 17.1,0 11,0 Z" fill="white" opacity="0.85"/><circle cx="11" cy="10" r="5" fill="url(#hiwLg2)"/><circle cx="11" cy="10" r="2.5" fill="white"/></g>
            </svg>
            <span className="font-display text-lg font-bold">Mouna</span>
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/login")} className="text-sm font-bold">Sign In</button>
            <button onClick={() => navigate("/login")} className="bg-white text-mouna-blue rounded-full px-4 py-2 text-sm font-bold">Get Started</button>
          </div>
        </nav>

        <div className="px-4 pt-12 pb-16 max-w-4xl mx-auto text-center">
          <div className="text-xs font-bold uppercase tracking-wider text-blue-200 mb-3">How Mouna Works</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-4 leading-tight">
            From request to done in <em className="text-yellow-300 not-italic">4 simple steps</em>
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto mb-8">
            We make it effortless to find trusted pros and grow service businesses in Houston.
          </p>

          {/* Tab toggle */}
          <div className="inline-flex bg-white/10 rounded-full p-1 backdrop-blur-sm">
            <button onClick={() => setActiveTab("client")}
              className={"px-6 py-2.5 rounded-full text-sm font-bold transition-all " + (activeTab === "client" ? "bg-white text-mouna-blue" : "text-white")}>
              I need help 🏠
            </button>
            <button onClick={() => setActiveTab("pro")}
              className={"px-6 py-2.5 rounded-full text-sm font-bold transition-all " + (activeTab === "pro" ? "bg-white text-mouna-blue" : "text-white")}>
              I'm a pro 🔧
            </button>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="px-4 py-16 max-w-5xl mx-auto">
        <div className="space-y-6">
          {steps.map((step, idx) => (
            <div key={step.num} className="bg-white rounded-3xl p-6 sm:p-8 shadow-card relative overflow-hidden">
              <div className={"absolute top-0 right-0 w-32 h-32 bg-gradient-to-br " + step.color + " opacity-5 rounded-full -mr-16 -mt-16"}></div>

              <div className="flex flex-col sm:flex-row items-start gap-6">
                <div className="flex-shrink-0 flex items-center gap-4">
                  <div className={"w-20 h-20 rounded-2xl bg-gradient-to-br " + step.color + " flex items-center justify-center text-4xl shadow-lg"}>
                    {step.icon}
                  </div>
                  <div className="text-5xl font-display font-bold text-gray-100">{step.num}</div>
                </div>

                <div className="flex-1">
                  <h3 className="font-display text-2xl font-bold text-navy mb-2">{step.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{step.description}</p>

                  <div className="flex flex-wrap gap-2">
                    {step.details.map(d => (
                      <span key={d} className="bg-gray-50 text-gray-600 text-xs font-bold rounded-full px-3 py-1.5">
                        ✓ {d}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {idx < steps.length - 1 && (
                <div className="hidden sm:block absolute left-14 -bottom-4 w-0.5 h-8 bg-gradient-to-b from-gray-200 to-transparent z-10"></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Trust bar */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl font-bold text-navy text-center mb-8">Built for Houston homeowners and pros</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl mb-1">🏠</div>
              <div className="text-xs text-gray-500 font-bold">Houston-local pros</div>
            </div>
            <div>
              <div className="text-3xl mb-1">🆓</div>
              <div className="text-xs text-gray-500 font-bold">Free for clients</div>
            </div>
            <div>
              <div className="text-3xl mb-1">💬</div>
              <div className="text-xs text-gray-500 font-bold">Direct messaging</div>
            </div>
            <div>
              <div className="text-3xl mb-1">⭐</div>
              <div className="text-xs text-gray-500 font-bold">Client reviews</div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="px-4 py-16 max-w-3xl mx-auto">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy text-center mb-2">Common questions</h2>
        <p className="text-gray-500 text-center mb-8">Everything you need to know about Mouna</p>

        <div className="space-y-3">
          {[
            { q: "Is Mouna free to use?", a: "Yes, completely free for clients. Pros pay a small fee only when they accept a lead." },
            { q: "How are pros vetted?", a: "Every pro is email-verified at signup and approved by the Mouna team before appearing on the platform. We don't run background, license, or insurance checks — if those matter for your job, ask your pro directly." },
            { q: "What if I'm not happy with the work?", a: "Talk to your pro first — most issues around pricing, scheduling, or quality are best resolved directly. Mouna doesn't hold funds or issue refunds, but you can share your experience in a review." },
            { q: "How do payments work?", a: "You pay your pro directly using whatever method you both agree on. Mouna doesn't process client payments — pros pay Mouna a small fee to accept leads." },
            { q: "Do you serve my area?", a: "We currently serve the Greater Houston area. We're expanding to more cities soon." },
          ].map((faq, i) => (
            <details key={i} className="bg-white rounded-2xl p-5 group">
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="font-bold text-navy">{faq.q}</span>
                <span className="text-mouna-blue text-2xl transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="text-gray-600 text-sm mt-3 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="px-4 py-12 max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-mouna-blue to-navy rounded-3xl p-8 sm:p-12 text-center text-white">
          <h2 className="font-display text-3xl font-bold mb-3">Ready to get started?</h2>
          <p className="text-blue-100 mb-6">Join thousands of homeowners and pros already on Mouna</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate("/login")}
              className="bg-white text-mouna-blue rounded-full px-8 py-3 font-bold hover:bg-blue-50 transition-colors">
              Find a Pro
            </button>
            <button onClick={() => navigate("/become-pro")}
              className="bg-transparent text-white border-2 border-white rounded-full px-8 py-3 font-bold hover:bg-white/10 transition-colors">
              Join as a Pro
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-4 text-center">
        <p className="text-xs text-gray-400">© 2026 Mouna. All rights reserved.</p>
      </footer>
    </div>
  );
}
